import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiBriefcase, FiUser } from 'react-icons/fi';
import toast from 'react-hot-toast';
import PageHeader from '../../components/ui/PageHeader';
import Button from '../../components/ui/Button';
import DataTable from '../../components/ui/DataTable';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { Input } from '../../components/ui/Input';
import { listCustomers, deleteCustomer } from '../../api/customers';
import { extractErrorMessage } from '../../api/client';
import CustomerDetailDrawer from './CustomerDetailDrawer';

export default function CustomersListPage() {
  const qc = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  
  const [deleteId, setDeleteId] = useState(null);

  const { data: customers = [], isLoading } = useQuery({
    queryKey: ['customers', 'all'],
    queryFn: listCustomers,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteCustomer,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers'] });
      toast.success('Customer deleted successfully');
      setDeleteId(null);
    },
    onError: (err) => {
      toast.error(extractErrorMessage(err) || 'Failed to delete customer');
    },
  });

  const filtered = customers.filter((c) => {
    const term = searchTerm.toLowerCase();
    const nameStr = `${c.firstName || ''} ${c.lastName || ''} ${c.companyName || ''}`.toLowerCase();
    const emailStr = (c.email || '').toLowerCase();
    const codeStr = (c.customerCode || '').toLowerCase();
    return nameStr.includes(term) || emailStr.includes(term) || codeStr.includes(term);
  });

  const handleCreate = () => {
    setSelectedCustomer(null);
    setIsDrawerOpen(true);
  };

  const handleEdit = (customer) => {
    setSelectedCustomer(customer);
    setIsDrawerOpen(true);
  };

  const columns = [
    {
      header: 'Customer',
      render: (c) => (
        <div>
          <div className="font-semibold text-slate-800 flex items-center gap-2">
            {c.customerType === 'INDIVIDUAL' ? <FiUser className="text-slate-400" /> : <FiBriefcase className="text-slate-400" />}
            {c.companyName || [c.firstName, c.lastName].filter(Boolean).join(' ')}
          </div>
          <div className="text-xs text-slate-500">{c.customerCode || `#${c.id}`}</div>
        </div>
      ),
    },
    {
      header: 'Contact Info',
      render: (c) => (
        <div className="text-sm">
          <div className="text-slate-700">{c.email || '—'}</div>
          <div className="text-slate-500">{c.phone || '—'}</div>
        </div>
      ),
    },
    {
      header: 'Type',
      render: (c) => (
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
          c.customerType === 'INDIVIDUAL' ? 'bg-blue-50 text-blue-700' : 'bg-indigo-50 text-indigo-700'
        }`}>
          {c.customerType === 'INDIVIDUAL' ? 'Individual' : 'Business'}
        </span>
      ),
    },
    {
      header: 'Location',
      render: (c) => {
        const parts = [c.billingCity, c.billingState].filter(Boolean);
        return <span className="text-sm text-slate-600">{parts.length > 0 ? parts.join(', ') : '—'}</span>;
      },
    },
    {
      header: 'Actions',
      render: (c) => (
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" icon={FiEdit2} onClick={() => handleEdit(c)}>
            Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            icon={FiTrash2}
            className="text-red-600 hover:bg-red-50 hover:text-red-700"
            onClick={() => setDeleteId(c.id)}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Customers"
        description="Manage your business and individual clients, their billing addresses, and tax information."
        actions={
          <Button variant="primary" icon={FiPlus} onClick={handleCreate}>
            Add Customer
          </Button>
        }
      />

      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
        <div className="flex gap-4 mb-4">
          <div className="relative w-72">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              className="pl-9"
              placeholder="Search customers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <DataTable
          columns={columns}
          rows={filtered}
          loading={isLoading}
          empty="No customers found matching your search."
        />
      </div>

      <CustomerDetailDrawer
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        customer={selectedCustomer}
      />

      <ConfirmDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        title="Delete Customer"
        message="Are you sure you want to delete this customer? This action cannot be undone."
        confirmText="Delete"
        isDanger
        onConfirm={() => {
          if (deleteId) deleteMutation.mutate(deleteId);
        }}
        loading={deleteMutation.isLoading}
      />
    </div>
  );
}
